import React, {
  forwardRef,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";
import { IconAlertCircle } from "@tabler/icons-react";

// Base interface for shared props
interface BaseInputProps {
  label?: string;
  icon?: React.ReactNode;
  error?: string;
  innerClassName?: string;
  endIcon?: React.ReactNode;
}

// Input-specific props
interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "className">,
    BaseInputProps {
  className?: string;
}

// Textarea-specific props
interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className">,
    BaseInputProps {
  className?: string;
  rows?: number;
}

// Shared wrapper component for input styling
const InputWrapper: React.FC<{
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ label, error, icon, endIcon, className, children }) => {
  return (
    <div className="">
      {label && (
        <label className="text-sm text-muted font-medium block mb-1">
          {label}
        </label>
      )}
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-3 rounded-lg border border-primaryborder bg-input-bgcolor transition-all",
          error
            ? "border-red-500/80 focus-within:border-red-500/80"
            : "focus-within:border-white/60",
          className
        )}
      >
        {icon && <div className="text-white flex-shrink-0">{icon}</div>}
        {children}
        {error ? (
          <div className="text-red-500/80 flex-shrink-0">
            <IconAlertCircle size={18} />
          </div>
        ) : (
          endIcon && (
            <div className="text-white/80 flex-shrink-0">{endIcon}</div>
          )
        )}
      </div>
      {error && <p className="text-red-500/80 text-sm mt-1">{error}</p>}
    </div>
  );
};

// Input component
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, icon, endIcon, error, className, innerClassName, ...props },
    ref
  ) => {
    return (
      <InputWrapper
        label={label}
        error={error}
        icon={icon}
        endIcon={endIcon}
        className={className}
      >
        <input
          ref={ref}
          className={cn(
            "bg-transparent w-full text-white outline-none placeholder:text-muted text-sm",
            innerClassName
          )}
          {...props}
        />
      </InputWrapper>
    );
  }
);

// Textarea component
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      icon,
      endIcon,
      error,
      className,
      innerClassName,
      rows = 3,
      ...props
    },
    ref
  ) => {
    return (
      <InputWrapper
        label={label}
        error={error}
        icon={icon}
        endIcon={endIcon}
        className={cn("items-start py-2", className)}
      >
        <textarea
          ref={ref}
          rows={rows}
          className={cn(
            "bg-transparent w-full text-white outline-none placeholder:text-muted text-sm min-h-[100px] max-h-[200px]",
            innerClassName
          )}
          {...props}
        />
      </InputWrapper>
    );
  }
);

Input.displayName = "Input";
Textarea.displayName = "Textarea";

export default Input;
export { Textarea, type InputProps, type TextareaProps };
